/**
 * Motivational Form — Utility Functions
 *
 * Question mapping, draft keys and the i18n binding for the shared rule set.
 *
 * The RULES themselves live in `@tonnie/heroes`
 * (`checkup/motivational-validator.ts`) and no longer live here. There used to
 * be three implementations of "is this answer good enough" — this file,
 * business-web's operator screen, and a third in @tonnie/heroes that nothing
 * imported — with different thresholds and no obligation to agree. What stays
 * here is only what is genuinely journey-web's: how a brain question becomes a
 * UI question, and how a machine code becomes localized copy.
 *
 * @module copilot/widgets/motivational-form
 */

import {
  MOTIVATIONAL_MAX_CHARS,
  MOTIVATIONAL_MIN_WORDS,
  countWords,
  motivationalQuestionRequired,
  validateMotivationalAnswerText,
} from '@tonnie/heroes';

import type { IBrainQuestion, IQuestion, IValidationResult } from './motivational-form.types';

export { countWords, MOTIVATIONAL_MAX_CHARS, MOTIVATIONAL_MIN_WORDS };

/** The shared rule set, under the name this widget's call sites already use. */
export const validateAnswer = validateMotivationalAnswerText;

// ── Brain → UI Question Mapper ──────────────────────────────────────────

/**
 * Maps brain scoring questions to the UI display shape.
 * Strips leading "#N" numbering from question text so the UI can render its own.
 *
 * `maxLength` is deliberately NOT derived from the brain's `token` array any
 * more. It used to be `max(token) × 4` — a user-facing character limit computed
 * from an AI context-window budget, which meant the citizen was shown an
 * arbitrary number nobody could explain, and (because the 4-chars-per-token
 * ratio is English-centric) a Portuguese author got less room for the same
 * budget. It also BLOCKED, defaulting to 800 characters when the brain sent no
 * token array, so a thoughtful long answer was simply refused. The ceiling is
 * now one explicit, generous, locale-neutral constant.
 */
export function mapBrainQuestionsToUI(brainQuestions: IBrainQuestion[]): IQuestion[] {
  return brainQuestions.map((q) => {
    // Strip leading "#N " numbering from question text — UI renders its own numbering
    const rawLabel = q.questionDesc || q.questionId;
    const label = rawLabel.replace(/^#\d+\s*/, '');

    return {
      questionId: q.questionId,
      label,
      required: motivationalQuestionRequired(),
      maxLength: MOTIVATIONAL_MAX_CHARS,
      concept: q.concept || undefined,
      context: q.context || undefined,
    };
  });
}

// ── Draft Key ───────────────────────────────────────────────────────────

export function draftKey(tenant?: string, persona?: string, journeyInstanceId?: string) {
  const base = `motivational_form_draft_${tenant}_${persona}`;
  return journeyInstanceId ? `${base}_${journeyInstanceId}` : base;
}

// ── Message binding ─────────────────────────────────────────────────────

/**
 * Resolve a validation result to a localized message. Kept out of the rule set
 * (pure / i18n-free) so the rules stay testable; the render site injects `t`
 * and the app-locale number formatter (`useLocale().formatNumber`, F34).
 */
export function validationMessage(
  result: IValidationResult,
  t: (key: string, opts?: Record<string, unknown>) => string,
  formatNumber: (value: number) => string
): string {
  switch (result.code) {
    case 'REQUIRED':
      return t('copilot.validation.required');
    case 'TOO_LONG':
      return t('copilot.validation.tooLong', {
        max: formatNumber(result.data?.maxChars ?? MOTIVATIONAL_MAX_CHARS),
      });
    case 'THIN':
      return t('copilot.validation.thin', {
        words: result.data?.wordCount ?? 0,
        min: MOTIVATIONAL_MIN_WORDS,
      });
    case 'OK':
      return t('copilot.validation.ok');
  }
}
